import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Info, RefreshCw, Send } from "lucide-react";
import segmentsService from "../services/segmentsService";
import customFieldsService from "../services/customFieldsService";
import { Segment, SegmentRules } from "../types/segment";
import { CustomFieldDefinition } from "../types/customFields";
import { selectOrganization } from "../store/slices/authUserSlice";
import SegmentRuleBuilder from "./SegmentRuleBuilder";
import SegmentSubscribersList from "./SegmentSubscribersList";
import SendToSegmentModal from "./SendToSegmentModal";

const DEFAULT_RULES: SegmentRules = {
  match: "all",
  conditions: [{ field: "status", operator: "equals", value: "Active" }],
};

const SegmentDetail: React.FC = () => {
  const { segmentId } = useParams<{ segmentId: string }>();
  const navigate = useNavigate();
  const isNew = segmentId === "new";

  const [name, setName] = useState("");
  const [segmentType, setSegmentType] = useState<"static" | "dynamic">("static");
  const [rules, setRules] = useState<SegmentRules>(DEFAULT_RULES);
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [segment, setSegment] = useState<Segment | null>(null);
  const [subscribersKey, setSubscribersKey] = useState(0);
  const [customFieldDefinitions, setCustomFieldDefinitions] = useState<CustomFieldDefinition[]>([]);
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);

  const organization = useSelector(selectOrganization);
  const canSendToSegment = !!organization?.feature_flags?.segment_sending_enabled;

  const handleSendSuccess = (success: boolean) => {
    setSendSuccess(success);
    if (success) {
      setTimeout(() => setSendSuccess(false), 3000);
    }
  };

  useEffect(() => {
    if (!isNew && segmentId) {
      loadSegment(segmentId);
    }
    loadCustomFieldDefinitions();
  }, [segmentId]);

  const loadCustomFieldDefinitions = async () => {
    try {
      const defs = await customFieldsService.listDefinitions();
      setCustomFieldDefinitions(defs);
    } catch (err) {
      console.error("Failed to load custom field definitions:", err);
    }
  };

  const hasCustomFieldConditions = useMemo(() => {
    return rules.conditions.some((c) => c.field.startsWith("cf_"));
  }, [rules.conditions]);

  const loadSegment = async (id: string) => {
    try {
      const data = await segmentsService.getSegment(id);
      setSegment(data);
      setName(data.name);
      setSegmentType(data.segment_type);
      setRules(data.rules);
      setPreviewCount(data.subscriber_count ?? null);
    } catch (err) {
      console.error("Failed to load segment:", err);
      setError("Segment not found");
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    try {
      const result = await segmentsService.previewSegment(rules);
      setPreviewCount(result.subscriber_count);
    } catch (err: any) {
      console.error("Preview failed:", err);
      setError(err.response?.data?.error || "Preview failed");
      setTimeout(() => setError(""), 3000);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Name is required");
      setTimeout(() => setError(""), 3000);
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const created = await segmentsService.createSegment(name.trim(), rules, segmentType);
        navigate(`/segments/${created.id}`, { replace: true });
      } else {
        const updated = await segmentsService.updateSegment(segmentId!, name.trim(), rules);
        setSegment(updated);
        setPreviewCount(updated.subscriber_count ?? null);
        setSubscribersKey((k) => k + 1);
      }
    } catch (err: any) {
      console.error("Save failed:", err);
      setError(err.response?.data?.messages?.join(", ") || err.response?.data?.error || "Save failed");
      setTimeout(() => setError(""), 5000);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this segment?")) return;

    setDeleting(true);
    try {
      await segmentsService.deleteSegment(segmentId!);
      navigate("/segments", { replace: true });
    } catch (err) {
      console.error("Delete failed:", err);
      setError("Failed to delete segment");
      setTimeout(() => setError(""), 3000);
    } finally {
      setDeleting(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const updated = await segmentsService.refreshSegment(segmentId!);
      setSegment(updated);
      setPreviewCount(updated.subscriber_count ?? null);
      setSubscribersKey((k) => k + 1);
    } catch (err: any) {
      console.error("Refresh failed:", err);
      setError(err.response?.data?.error || "Refresh failed");
      setTimeout(() => setError(""), 3000);
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return <div style={{ padding: "20px" }}>Loading segment...</div>;
  }

  return (
    <div style={{ padding: "20px", maxWidth: "1000px" }}>
      <div style={{ marginBottom: "20px" }}>
        <button
          onClick={() => navigate("/segments")}
          style={{
            padding: "4px 0",
            border: "none",
            background: "none",
            cursor: "pointer",
            color: "#6b7280",
            fontSize: "14px",
          }}
        >
          &larr; Back to Segments
        </button>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
        }}
      >
        <h1 style={{ margin: 0 }}>{isNew ? "Create Segment" : "Edit Segment"}</h1>
        <div style={{ display: "flex", gap: "8px" }}>
          {!isNew && segment && canSendToSegment && (
            <button
              onClick={() => setIsSendModalOpen(true)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "8px 16px",
                backgroundColor: "#fff",
                color: "#065f46",
                border: "1px solid #065f46",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              <Send size={14} />
              Send Newsletter
            </button>
          )}
          {!isNew && segment?.segment_type === "dynamic" && (
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "8px 16px",
                backgroundColor: "#fff",
                color: "#374151",
                border: "1px solid #d1d5db",
                borderRadius: "4px",
                cursor: refreshing ? "not-allowed" : "pointer",
                opacity: refreshing ? 0.5 : 1,
              }}
            >
              <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
              {refreshing ? "Refreshing..." : "Refresh"}
            </button>
          )}
          {!isNew && !segment?.is_default && (
            <button
              onClick={handleDelete}
              disabled={deleting}
              style={{
                padding: "8px 16px",
                backgroundColor: "#fff",
                color: "#dc2626",
                border: "1px solid #dc2626",
                borderRadius: "4px",
                cursor: deleting ? "not-allowed" : "pointer",
                opacity: deleting ? 0.5 : 1,
              }}
            >
              {deleting ? "Deleting..." : "Delete"}
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            style={{
              padding: "8px 16px",
              backgroundColor: "#000",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: saving ? "not-allowed" : "pointer",
              opacity: saving || !name.trim() ? 0.5 : 1,
            }}
          >
            {saving ? "Saving..." : isNew ? "Create" : "Save"}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ color: "red", marginBottom: "16px" }}>{error}</div>
      )}

      <div style={{ marginBottom: "24px" }}>
        <label style={{ display: "block", fontSize: "14px", fontWeight: 500, marginBottom: "6px", color: "#374151" }}>
          Segment Name
        </label>
        <input
          type="text"
          placeholder="e.g., Highly Engaged"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={segment?.is_default}
          style={{
            width: "100%",
            padding: "8px 12px",
            border: "1px solid #d1d5db",
            borderRadius: "4px",
            fontSize: "14px",
            boxSizing: "border-box",
            maxWidth: "400px",
            opacity: segment?.is_default ? 0.6 : 1,
            cursor: segment?.is_default ? "not-allowed" : "text",
          }}
        />
      </div>

      {isNew && (
        <div style={{ marginBottom: "24px" }}>
          <label style={{ display: "block", fontSize: "14px", fontWeight: 500, marginBottom: "6px", color: "#374151" }}>
            Segment Type
          </label>
          <div style={{ display: "flex", gap: "16px" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", fontSize: "14px" }}>
              <input
                type="radio"
                name="segmentType"
                value="dynamic"
                checked={segmentType === "dynamic"}
                onChange={() => setSegmentType("dynamic")}
              />
              Dynamic
              <span style={{ color: "#6b7280", fontSize: "12px" }}>
                — Recomputed nightly to match rules
              </span>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", fontSize: "14px" }}>
              <input
                type="radio"
                name="segmentType"
                value="static"
                checked={segmentType === "static"}
                onChange={() => setSegmentType("static")}
              />
              Static
              <span style={{ color: "#6b7280", fontSize: "12px" }}>
                — Computed once from rules, not recomputed
              </span>
            </label>
          </div>
        </div>
      )}

      {!isNew && segment && (
        <div style={{ marginBottom: "24px" }}>
          <span style={{
            display: "inline-block",
            padding: "4px 10px",
            borderRadius: "12px",
            fontSize: "12px",
            fontWeight: 500,
            backgroundColor: segment.segment_type === "static" ? "#dbeafe" : "#d1fae5",
            color: segment.segment_type === "static" ? "#1e40af" : "#065f46",
          }}>
            {segment.segment_type === "static" ? "Static" : "Dynamic"}
          </span>
        </div>
      )}

      <div
        style={{
          marginBottom: "24px",
          padding: "16px",
          backgroundColor: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: "8px",
        }}
      >
        <h3 style={{ margin: "0 0 12px 0" }}>Rules</h3>
        <SegmentRuleBuilder
          rules={rules}
          onChange={setRules}
          onPreview={handlePreview}
          previewCount={previewCount}
          previewLoading={previewLoading}
          customFieldDefinitions={customFieldDefinitions}
        />
        {hasCustomFieldConditions && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginTop: "12px",
              padding: "8px 12px",
              backgroundColor: "#eff6ff",
              border: "1px solid #bfdbfe",
              borderRadius: "6px",
              fontSize: "13px",
              color: "#1e40af",
            }}
          >
            <Info size={14} />
            Custom field data may take up to 1 hour to reflect in segment results after being updated.
          </div>
        )}
      </div>

      {!isNew && segmentId && (
        <SegmentSubscribersList
          key={subscribersKey}
          segmentId={segmentId}
          segmentType={segment?.segment_type || "dynamic"}
        />
      )}

      {sendSuccess && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            right: "20px",
            padding: "12px 20px",
            backgroundColor: "#e0f7df",
            color: "#2e7d32",
            border: "1px solid #2e7d32",
            borderRadius: "8px",
            zIndex: 1000,
          }}
        >
          Campaign queued successfully.
        </div>
      )}

      {isSendModalOpen && segment && (
        <SendToSegmentModal
          segment={segment}
          onClose={() => setIsSendModalOpen(false)}
          setSendToSegmentSuccess={handleSendSuccess}
        />
      )}
    </div>
  );
};

export default SegmentDetail;
